import clsx from "clsx"
import { type ReactNode, useState } from "react"

import type { AssignedTo, StandupBundle, StandupGroup, StandupItem, StandupTask } from "@/types/sprint-report"

interface Props {
  groups: StandupGroup[]
}

/* ------------------------------------------------------------------ helpers */

function ownerName(o: AssignedTo | null | undefined): string {
  return o?.name ?? "Unassigned"
}

function isDone(state: string, category?: string): boolean {
  const s = state.trim().toLowerCase()
  return category === "done" || s === "done" || s === "closed"
}

/** Status tone — semantic colour for the pill + the card accent. */
type Tone = "todo" | "ready" | "dev" | "devdone" | "qa" | "done"
function stateTone(state: string, category?: string): Tone {
  const s = state.trim().toLowerCase()
  if (isDone(state, category)) return "done"
  if (s.includes("qa")) return "qa"
  if (s === "dev done" || category === "dev_complete") return "devdone"
  if (
    category === "in_dev" ||
    s === "in progress" ||
    s === "committed" ||
    s.includes("dev wip") ||
    s.includes("dev pr")
  )
    return "dev"
  if (s.includes("ready")) return "ready"
  return "todo"
}

const PILL: Record<Tone, string> = {
  todo: "bg-slate-100 text-slate-600 ring-slate-200",
  ready: "bg-indigo-100 text-indigo-700 ring-indigo-200",
  dev: "bg-sky-100 text-sky-800 ring-sky-200",
  devdone: "bg-teal-100 text-teal-700 ring-teal-300",
  qa: "bg-amber-100 text-amber-900 ring-amber-300",
  done: "bg-emerald-100 text-emerald-700 ring-emerald-300",
}

/** Faint header wash keyed to readiness, so a group reads its state at a glance. */
const HEAD_TINT: Record<Tone, string> = {
  todo: "from-slate-50/80",
  ready: "from-indigo-50/70",
  dev: "from-sky-50/80",
  devdone: "from-teal-50/70",
  qa: "from-amber-50/80",
  done: "from-emerald-50/70",
}
const ACCENT: Record<Tone, string> = {
  todo: "bg-slate-300",
  ready: "bg-indigo-400",
  dev: "bg-blue-500",
  devdone: "bg-teal-500",
  qa: "bg-amber-400",
  done: "bg-emerald-500",
}

/** Strip the redundant bundle title from a task title → "[Implementation]". */
function cleanTaskTitle(title: string, bundleTitle?: string): string {
  let t = title.trim()
  if (bundleTitle) {
    const bt = bundleTitle.trim()
    for (const v of [` - ${bt}`, ` – ${bt}`, ` ${bt}`, `- ${bt}`]) {
      if (t.toLowerCase().endsWith(v.toLowerCase())) {
        t = t.slice(0, -v.length).trim()
        break
      }
    }
  }
  return t.replace(/[-–—\s]+$/, "").trim() || title
}

/** Drop a leading priority token ("[P1-A] …") — it's shown as a chip instead. */
function stripPriorityPrefix(title: string): string {
  return title.replace(/^\s*\[\s*P[0-3][^\]]*\]\s*/i, "").trim() || title
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/** Drop a leading bundle code ("G16a …" / "[G7-B] …") — shown as a chip instead. */
function stripLeadingCode(title: string, code: string): string {
  if (!code) return title
  const re = new RegExp(`^\\s*\\[?\\s*${escapeRe(code)}\\s*\\]?\\s+`, "i")
  return title.replace(re, "").trim() || title
}

/** Tasks: active first, Done last; In Progress above To Do. */
function sortTasks(tasks: StandupTask[]): StandupTask[] {
  const rank = (t: StandupTask) => {
    if (isDone(t.state, t.state_category)) return 0
    const s = t.state.trim().toLowerCase()
    if (s === "in progress") return 3
    if (s === "to do" || s === "new") return 2
    return 1
  }
  return [...tasks].sort((a, b) => rank(b) - rank(a))
}

const AVATAR_COLORS = [
  "#2563eb", "#0e9f6e", "#d9772b", "#7c3aed", "#db2777",
  "#0891b2", "#ca8a04", "#dc2626", "#4f46e5", "#15803d",
]
function avatarColor(name: string): string {
  let h = 0
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}
function initials(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  return ((parts[0][0] ?? "") + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase()
}

/* ----------------------------------------------------------------- atoms */

export function Avatar({ name, dim }: { name: string; dim?: boolean }) {
  if (name === "Unassigned") {
    return (
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-dashed border-slate-300 text-[9px] text-slate-400">
        ?
      </span>
    )
  }
  return (
    <span
      className={clsx(
        "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white",
        dim && "opacity-60",
      )}
      style={{ backgroundColor: avatarColor(name) }}
      title={name}
    >
      {initials(name)}
    </span>
  )
}

function StatePill({ state, category }: { state: string; category?: string }) {
  const tone = stateTone(state, category)
  return (
    <span
      className={clsx(
        "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1",
        PILL[tone],
      )}
    >
      {state}
    </span>
  )
}

const PRIORITY_BADGE: Record<string, string> = {
  P0: "bg-priority-p0 text-white",
  P1: "bg-priority-p1 text-white",
  P2: "bg-priority-p2 text-white",
  P3: "bg-priority-p3 text-white",
}
/** Priority shown as a chip (replaces the "[P1-A]" text prefix in titles). */
function PriBadge({ raw, major }: { raw: string; major: string }) {
  return (
    <span
      className={clsx(
        "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold leading-none",
        PRIORITY_BADGE[major] ?? "bg-slate-200 text-slate-700",
      )}
    >
      {raw}
    </span>
  )
}

/** Small bundle-code chip on member rows (replaces the "[G7-B]" text prefix). */
function CodeChip({ code }: { code: string }) {
  return (
    <span className="shrink-0 rounded bg-slate-600 px-1 py-0.5 text-[9px] font-bold leading-none tracking-wide text-white">
      {code}
    </span>
  )
}

/** ADO-style colored type glyph (matches Azure Boards icon language). */
export function TypeIcon({ type }: { type: string }) {
  const t = type.trim().toLowerCase()
  const sq = "inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px]"
  // Bug → red ladybug (ADO).
  if (t === "bug")
    return (
      <span
        className="inline-flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-[#cc293d]"
        title="Bug"
      >
        <svg viewBox="0 0 24 24" className="h-[12px] w-[12px]">
          <line x1="12" y1="5" x2="12" y2="19" stroke="white" strokeWidth="1.3" />
          <circle cx="8.4" cy="11" r="1.15" fill="white" />
          <circle cx="15.6" cy="11" r="1.15" fill="white" />
        </svg>
      </span>
    )
  // Task → gold checkmark.
  if (t === "task")
    return (
      <span className={clsx(sq, "bg-[#f2b300]")} title="Task">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="white" strokeWidth="3.6">
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    )
  // Product Backlog Item → blue list.
  if (t === "product backlog item")
    return (
      <span className={clsx(sq, "bg-[#009ccc]")} title="Product Backlog Item">
        <svg viewBox="0 0 24 24" className="h-3 w-3" stroke="white" strokeWidth="2.8">
          <path d="M5 7h14M5 12h14M5 17h9" strokeLinecap="round" />
        </svg>
      </span>
    )
  // Project Item → green package/box.
  if (t === "project item")
    return (
      <span className={clsx(sq, "bg-[#107c41]")} title="Project Item">
        <svg viewBox="0 0 24 24" className="h-[13px] w-[13px]" fill="white">
          <path d="M12 2l8 4.2v9.6L12 20l-8-4.2V6.2z" opacity="0.95" />
          <path d="M4 6.2l8 4.2 8-4.2" fill="none" stroke="#107c41" strokeWidth="1.4" />
        </svg>
      </span>
    )
  // Feature (bundle) → purple trophy.
  if (t === "feature")
    return (
      <span className={clsx(sq, "bg-[#773b93]")} title="Feature">
        <svg viewBox="0 0 24 24" className="h-3 w-3" fill="white">
          <path d="M6 3h12v3a6 6 0 11-12 0V3z" />
          <rect x="10.6" y="11.5" width="2.8" height="4" />
          <rect x="7" y="17" width="10" height="2.4" rx="0.5" />
        </svg>
      </span>
    )
  return <span className={clsx(sq, "bg-slate-400")} title={type} />
}

/* ---------------------------------------------------------------- rows */

// Every row caps at the same width with the same fixed right-hand columns, so
// status + owner line up in clean vertical lanes. A dotted leader bridges the
// gap from the (short) title to those lanes — no dead band to scan across.
const ROW_MAX = "max-w-[82rem]"
const W_STATUS = "w-[6.5rem]"
const W_OWNER = "w-[9.5rem]"

/** Dotted leader filling title → status, so the eye tracks across without a void. */
function Leader() {
  return (
    <span
      aria-hidden
      className="mx-1 h-px flex-1 self-center border-t border-dotted border-slate-200 transition-colors group-hover/row:border-slate-300"
    />
  )
}

function Effort({ value, unit = "" }: { value: number | null; unit?: string }) {
  return (
    <span className="num w-8 shrink-0 text-right text-[11px] tabular-nums text-slate-400">
      {value == null ? <span className="text-slate-300">·</span> : `${value}${unit}`}
    </span>
  )
}

function StatusCell({ state, category }: { state: string; category?: string }) {
  return (
    <span className={clsx("flex shrink-0 justify-start", W_STATUS)}>
      <StatePill state={state} category={category} />
    </span>
  )
}

function OwnerCell({ name, dim, strong }: { name: string; dim?: boolean; strong?: boolean }) {
  return (
    <span className={clsx("flex shrink-0 items-center gap-1.5", W_OWNER)}>
      <Avatar name={name} dim={dim} />
      <span className={clsx("truncate text-[11px]", strong ? "font-semibold text-ink" : "text-slate-600")}>
        {name}
      </span>
    </span>
  )
}

/** Meta gutter (rail · icon · id) — id sits right after the icon so it aligns
 *  across every row type (work items add their chips after it; the rail slot
 *  marks an item's child task). */
function Meta({
  icon,
  id,
  url,
  rail,
}: {
  icon: ReactNode
  id: number
  url: string
  rail?: boolean
}) {
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <span className="flex w-3 justify-center text-sm leading-none text-slate-300">
        {rail ? "└" : ""}
      </span>
      {icon}
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="num w-10 shrink-0 text-[11px] tabular-nums text-slate-400 hover:text-blue-700 hover:underline"
      >
        {id}
      </a>
    </span>
  )
}

function TaskRow({
  task,
  bundleTitle,
  nested = false,
}: {
  task: StandupTask
  bundleTitle?: string
  nested?: boolean
}) {
  const done = isDone(task.state, task.state_category)
  return (
    <div
      className={clsx(
        // Indented one level so tasks read as subordinate to the work items above.
        "group/row flex w-full items-center gap-2 rounded-md py-1 pl-10 pr-2 text-xs transition-colors hover:bg-slate-50/80",
        ROW_MAX,
        done && "opacity-55",
      )}
    >
      <Meta icon={<TypeIcon type="Task" />} id={task.id} url={task.url} rail={nested} />
      <span className="min-w-0 max-w-[52rem] shrink truncate font-medium text-slate-700">
        {cleanTaskTitle(task.title, bundleTitle)}
      </span>
      <Leader />
      <Effort value={task.effort} unit="h" />
      <StatusCell state={task.state} category={task.state_category} />
      <OwnerCell name={ownerName(task.assigned_to)} dim={done} />
    </div>
  )
}

function ItemRow({ item, code }: { item: StandupItem; code?: string }) {
  const done = isDone(item.state, item.state_category)
  // Both the priority and the bundle code show as chips, so strip both from the
  // title (priority twice, in case the code prefix came first).
  let title = stripPriorityPrefix(item.title)
  if (code) title = stripLeadingCode(title, code)
  title = stripPriorityPrefix(title)
  return (
    <div>
      <div
        className={clsx(
          "group/row flex w-full items-center gap-2 rounded-md py-1.5 pl-1.5 pr-2 transition-colors hover:bg-slate-50/80",
          ROW_MAX,
          done && "opacity-55",
        )}
      >
        <Meta icon={<TypeIcon type={item.work_item_type} />} id={item.id} url={item.url} />
        <span className="flex shrink-0 items-center gap-1">
          <PriBadge raw={item.priority.raw} major={item.priority.major} />
          {code && <CodeChip code={code} />}
        </span>
        <span className="min-w-0 max-w-[52rem] shrink truncate text-[13px] font-semibold text-ink">
          {title}
        </span>
        <Leader />
        <Effort value={item.effort} />
        <StatusCell state={item.state} category={item.state_category} />
        <OwnerCell name={ownerName(item.assigned_to)} dim={done} />
      </div>
      {item.tasks && item.tasks.length > 0 &&
        sortTasks(item.tasks).map((t) => <TaskRow key={t.id} task={t} nested />)}
    </div>
  )
}

/** Mini done-progress meter — the at-a-glance "how close is this group". */
function Progress({ done, total }: { done: number; total: number }) {
  if (total <= 0) return null
  const pct = Math.round((done / total) * 100)
  return (
    <span className="flex items-center gap-1.5" title={`${done} of ${total} done`}>
      <span className="relative h-1.5 w-14 overflow-hidden rounded-full bg-slate-200">
        <span
          className={clsx(
            "absolute inset-y-0 left-0 rounded-full transition-all",
            pct === 100 ? "bg-emerald-500" : "bg-emerald-400",
          )}
          style={{ width: `${pct}%` }}
        />
      </span>
      <span className="num text-[10px] tabular-nums text-slate-500">
        {done}/{total}
      </span>
    </span>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className={clsx("flex items-center gap-2 pb-0.5 pl-1 pt-2", ROW_MAX)}>
      <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
        {children}
      </span>
      <span className="h-px flex-1 bg-slate-100" />
    </div>
  )
}

/* ---------------------------------------------------------------- group */

function groupId(g: StandupGroup, i: number): string {
  return g.kind === "bundle" ? `b${g.bundle?.id}` : `i${g.item?.id}-${i}`
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={clsx(
        "h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform duration-200",
        open && "rotate-90",
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
    >
      <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function GroupRow({
  group,
  open,
  onToggle,
}: {
  group: StandupGroup
  open: boolean
  onToggle: () => void
}) {
  const isBundle = group.kind === "bundle"
  const head = (isBundle ? group.bundle : group.item) as StandupBundle | StandupItem | undefined
  if (!head) return null

  const headState = isBundle ? group.bundle!.state : group.item!.state
  const headCat = isBundle ? undefined : group.item!.state_category
  // Identifier (bundle code / priority) shows as a chip, so strip its text from
  // the title — only ever strip what the chip already carries.
  const code = isBundle ? group.bundle?.code ?? "" : ""
  const headTitle = isBundle
    ? stripLeadingCode(group.bundle!.title, code)
    : stripPriorityPrefix(group.item!.title)
  const tone = stateTone(headState, headCat)
  const members = isBundle ? (group.items ?? []) : []
  const tasks = sortTasks(group.tasks)
  const ownerLabel = ownerName(group.owner)
  // Progress tracks task completion (the actual unit of work); fall back to
  // work-item completion only for groups that have no tasks.
  const doneTasks = tasks.filter((t) => isDone(t.state, t.state_category)).length
  const doneMembers = members.filter((m) => isDone(m.state, m.state_category)).length
  const progressDone = tasks.length > 0 ? doneTasks : doneMembers
  const progressTotal = tasks.length > 0 ? tasks.length : members.length

  return (
    <div className="flex">
      <div className={clsx("w-1 shrink-0", ACCENT[tone])} />
      <div className="min-w-0 flex-1">
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onToggle()
            }
          }}
          className={clsx(
            "group/row cursor-pointer bg-gradient-to-r to-transparent px-3 py-2.5 transition-colors",
            open ? HEAD_TINT[tone] : "from-transparent hover:from-slate-50/90",
          )}
        >
          <div className={clsx("flex w-full items-center gap-2.5 pr-2", ROW_MAX)}>
            <Chevron open={open} />
            <TypeIcon type={isBundle ? "Feature" : group.item!.work_item_type} />
            {isBundle && code && (
              <span className="shrink-0 rounded bg-ink px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-white">
                {code}
              </span>
            )}
            {!isBundle && (
              <PriBadge raw={group.item!.priority.raw} major={group.item!.priority.major} />
            )}
            <StatePill state={headState} category={headCat} />
            <a
              href={head.url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="min-w-0 max-w-[56rem] shrink truncate font-display text-sm font-bold text-ink hover:underline"
            >
              {headTitle}
            </a>
            <Leader />
            <Progress done={progressDone} total={progressTotal} />
            <OwnerCell name={ownerLabel} strong />
          </div>
        </div>

        {open && (
          <div className="animate-rise space-y-0.5 px-3 pb-3 pt-1">
            {isBundle && members.length > 0 && (
              <>
                <SectionLabel>Work items · {members.length}</SectionLabel>
                {members.map((m) => (
                  <ItemRow key={m.id} item={m} code={group.bundle?.code ?? undefined} />
                ))}
              </>
            )}
            {tasks.length > 0 && (
              <>
                {isBundle && <SectionLabel>Tasks · {tasks.length}</SectionLabel>}
                {tasks.map((t) => (
                  <TaskRow
                    key={t.id}
                    task={t}
                    bundleTitle={isBundle ? group.bundle?.title : group.item?.title}
                  />
                ))}
              </>
            )}
            {members.length === 0 && tasks.length === 0 && (
              <div className="px-2 py-1.5 text-[11px] italic text-slate-400">
                No active tasks or items.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- root */

export function Standup({ groups }: Props) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  if (groups.length === 0) {
    return (
      <p className="rounded border border-slate-300 p-6 text-center text-sm text-slate-500">
        No current-sprint work to show.
      </p>
    )
  }

  const ids = groups.map((g, i) => groupId(g, i))
  const allCollapsed = ids.every((id) => collapsed.has(id))
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  return (
    <div className="space-y-2.5">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
        <span>Grouped by bundle / item · ordered by readiness. Click a header to fold.</span>
        <button
          type="button"
          onClick={() => setCollapsed(allCollapsed ? new Set() : new Set(ids))}
          className="rounded-full border border-slate-200 bg-white/60 px-2.5 py-0.5 font-medium text-slate-500 transition hover:text-ink"
        >
          {allCollapsed ? "Expand all" : "Collapse all"}
        </button>
        <span className="ml-auto flex items-center gap-3">
          <Legend type="Bug" label="Bug" />
          <Legend type="Product Backlog Item" label="PBI" />
          <Legend type="Project Item" label="Project Item" />
          <Legend type="Task" label="Task" />
        </span>
      </div>
      <div className="divide-y divide-slate-200/70 overflow-hidden rounded-xl border border-slate-200/80 bg-white/80 shadow-card">
        {groups.map((g, i) => {
          const id = groupId(g, i)
          return (
            <GroupRow key={id} group={g} open={!collapsed.has(id)} onToggle={() => toggle(id)} />
          )
        })}
      </div>
    </div>
  )
}

function Legend({ type, label }: { type: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      <TypeIcon type={type} />
      {label}
    </span>
  )
}
